import { useFormContext, Controller } from "react-hook-form";
import DateSelect from "@/components/DateSelect";
import TextField from "@/components/TextField/TextField";
import type { FormValues } from "../validation/schema";
import { RateUnit } from "../../../types";
import { RATE_UNIT_MAP } from "../../../_utils";

interface ActivityDatesStepProps {
  rateUnit?: RateUnit;
}

export default function ActivityDatesStep({ rateUnit }: ActivityDatesStepProps) {
  const {
    control,
    watch,
    formState: { errors }
  } = useFormContext<FormValues>();

  const startDate = watch("startDate");
  const unitLabel = rateUnit ? RATE_UNIT_MAP[rateUnit] : "hr";

  return (
    <div className="flex flex-col gap-6">
      <div>
        <p className="text-title-large">When is/was this activity?</p>
        <p className="text-body-medium text-sys-brand-on-surface-variant mt-6">
          Leave the end date blank if this activity is recurring.
        </p>
      </div>

      {rateUnit && rateUnit !== RateUnit.INSTANCE && (
        <Controller
          name="duration"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              value={field.value ?? ""}
              label={`Length of Activity (${unitLabel})`}
              required
              type="number"
              step="0.1"
              min="0"
              error={!!fieldState.error}
              errorText={fieldState.error?.message}
            />
          )}
        />
      )}

      <Controller
        name="startDate"
        control={control}
        render={({ field }) => (
          <DateSelect
            label="Start Date *"
            value={field.value || { startDate: "", endDate: "" }}
            useSingleDate
            popoverDirection="down"
            onChange={(value) => field.onChange(value)}
            error={!!errors.startDate}
            errorMessage={errors.startDate?.message as string}
          />
        )}
      />

      <Controller
        name="endDate"
        control={control}
        render={({ field }) => (
          <DateSelect
            label="End Date"
            value={{
              startDate: field.value?.startDate ?? "",
              endDate: field.value?.endDate ?? ""
            }}
            useSingleDate
            popoverDirection="down"
            minDate={
              startDate?.startDate
                ? (() => {
                    const [y, m, d] = startDate.startDate.split("-").map(Number);
                    return new Date(y, m - 1, d);
                  })()
                : undefined
            }
            onChange={(value) => field.onChange(value)}
            error={!!errors.endDate}
            errorMessage={errors.endDate?.message as string}
          />
        )}
      />
    </div>
  );
}
